import {
  BulkCreateShiftmanagementUnassignedShiftUnassignedShiftsInput,
  ShiftManagementBulkCreateUnassignedShiftsDocument,
} from 'generated/resources/graphql'
import type moment from 'moment'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import { trackAddUnassignedEvent } from 'modules/shift_management/lib/trackEvents'
import { updateUnassignedShiftsCoverageCache } from 'modules/shift_management/utils/apolloCache/unassignedShiftsCoverage'

import { buildUnassignedShiftsParams } from './useAddUnassignedShifts'
import { useSchedule } from './useSchedule'
import { useFilters } from './useFilters'
export interface Props {
  startDate: moment.Moment
  endDate: moment.Moment
  staffRequirements: Array<{
    job_catalog_roles_ids: string[]
    job_catalog_tree_role_uuids: string[]
    staff_count: number
  }>
  forms: UnassignedShiftForm[]
  onSuccess?: () => void
}

const useAddUnassignedDefaultShifts = ({
  startDate,
  endDate,
  staffRequirements,
  forms,
  onSuccess,
}: Props) => {
  const [{ view }] = useSchedule()
  const [{ locationIds }] = useFilters()
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()

  const [addUnassignedShifts, { loading }] = useMutation(
    ShiftManagementBulkCreateUnassignedShiftsDocument,
    {
      extractorFn: (data) => data.shiftManagementMutations.bulkCreateShiftmanagementUnassignedShift,
      update: (cache, { data }) => {
        const newUnassignedShifts =
          data?.shiftManagementMutations.bulkCreateShiftmanagementUnassignedShift.unassignedShifts
        if (!newUnassignedShifts || newUnassignedShifts.length === 0) return

        updateUnassignedShiftsCoverageCache({
          cache,
          startAt: view.startAt,
          endAt: view.endAt,
          locationIds,
          unassignedShifts: newUnassignedShifts,
        })
      },
    }
  )

  const buildParameters = (): BulkCreateShiftmanagementUnassignedShiftUnassignedShiftsInput[] => {
    return forms.flatMap((form: UnassignedShiftForm) => {
      return buildUnassignedShiftsParams({
        form,
        companyId: currentCompanyId,
        startDate,
        endDate,
        staffRequirements,
      })
    })
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const unassignedShiftParams = buildParameters()
    trackAddUnassignedEvent({
      unassignedShiftParams,
      isDefaultShift: true,
      colors: forms.map((f) => f.get('color').value),
    })
    const result = await addUnassignedShifts({
      variables: { unassignedShifts: unassignedShiftParams },
    })

    const handled = handleResult(result, (_errors) => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('shift_management.actions.add_unassigned_shifts_success'),
      })

      closePortal()

      if (onSuccess) onSuccess()
    }
  }

  return {
    submitting: loading,
    onSubmit,
  }
}

export { useAddUnassignedDefaultShifts }
